import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsEnvelope, BsPencilSquare, BsPerson } from 'react-icons/bs';
import { CiCircleInfo } from 'react-icons/ci';
import { FaCrown, FaRegTrashAlt, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import Button from '@/components/Button';
import LoadingOverlay from '@/components/LoadingOverlay';
import PhoneNumberInput, {
  type PhoneNumberInputValue,
} from '@/components/PhoneNumberInput';
import ResponsiveModalBottomSheet from '@/components/ResponsiveModalBottomSheet';
import StickyFooter from '@/components/StickyFooter';
import TextInput from '@/components/TextInput';
import { showErrorToast } from '@/components/toastStore';
// import moved below to satisfy import ordering
import {
  DepositMode,
  PackageType,
  PaymentMethodType,
  PaymentType,
  type BookingPaymentLocationState,
} from '@/models';
import AppointmentInfo from '@/pages/components/AppointmentInfo';
import BookingCostSummary from '@/pages/components/BookingCostSummary';
import BookingDetails from '@/pages/components/BookingDetails';
import {
  NoPackageBottomSheet,
  OTPBottomSheet,
} from '@/pages/components/bottomsheets';
import {
  BookingService,
  getLiffProfile,
  ProfileService,
  useStoredProfile,
} from '@/services';
import { useBookingStore } from '@/store/bookingStore';
import { useBrandStore } from '@/store/brandStore';
import { useUserStore } from '@/store/userStore';
import { getApiErrorMessage } from '@/utils/error';
import { phoneSuperRefine } from '@/utils/validation';

import CancellationPolicyModal from './components/CancellationPolicyModal';
import CustomerDetailsFormModalBottomSheet, {
  type CustomerDetails,
} from './components/CustomerDetailsFormModalBottomSheet';
import PackageSelectionModalPage from './components/PackageSelectionModalPage';
import PromptPayPaymentModalPage from './components/PromptPayPaymentModalPage';

// Payment Method Option Component
interface PaymentMethodOptionProps {
  id: PaymentMethodType;
  label: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}

const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({
  id,
  label,
  icon,
  isSelected,
  onSelect,
  disabled = false,
}) => {
  return (
    <label
      className={`rounded-lg border p-3 transition-colors ${
        isSelected
          ? 'border-primary bg-bg-selected border-1'
          : 'border-border-inactive border bg-white'
      } flex cursor-pointer items-center gap-3`}
    >
      <input
        type="radio"
        value={id.toString()}
        className="sr-only"
        checked={isSelected}
        onChange={onSelect}
        disabled={disabled}
      />
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <span className="leading-6 text-black">{label}</span>
        </div>
        <div className="text-text-muted flex-shrink-0">{icon}</div>
      </div>
    </label>
  );
};

// Helper to create schema based on payment type requirement
const createPaymentFormSchema = (requirePaymentMethod: boolean) => {
  const baseSchema = z
    .object({
      tel: z.string().optional(),
      telPrefix: z.string().optional(),
      name: z.string().min(1, { error: 'validation.required' }),
      email: z
        .string()
        .min(1, { error: 'validation.required' })
        .check(z.email({ error: 'validation.invalidEmail' })),
      paymentMethod: z
        .number()
        .int({ message: 'payment.paymentMustBeInteger' })
        .min(1, { message: 'payment.paymentRequired' })
        .optional(),
    })
    .superRefine(phoneSuperRefine('tel', 'telPrefix', '+66'));

  if (requirePaymentMethod) {
    return baseSchema.refine(
      (data) => data.paymentMethod !== undefined && data.paymentMethod > 0,
      {
        message: 'payment.paymentRequired',
        path: ['paymentMethod'],
      },
    );
  }

  return baseSchema;
};

type PaymentFormData = z.infer<ReturnType<typeof createPaymentFormSchema>>;

const PaymentSummary: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    customers,
    availabilityId,
    updateCustomer,
    paymentMethod: storePaymentMethod,
    balance,
    setContactInfo,
    selectedPackages,
    setPaymentMethod,
    setBookingCode,
    name: contactName,
    email: contactEmail,
    tel: contactTel,
    telPrefix: contactTelPrefix,
    storeId,
  } = useBookingStore();

  const {
    getPaymentType,
    getShowPackageUsage,
    getDepositMode,
    setBrand,
    setStoreId: setBrandStoreId,
    getShowCancellationPolicy,
  } = useBrandStore();
  const verifiedPhoneNumber = useUserStore((state) => state.phoneNumber);
  const storedProfile = useStoredProfile();
  const isLoggedIn = ProfileService.isLoggedIn();
  const paymentType = getPaymentType();
  const depositMode = getDepositMode();
  const requirePaymentMethod =
    paymentType !== PaymentType.None && balance.total > 0;
  const isDeposit =
    paymentType === PaymentType.Deposit && balance.depositTotal < balance.total;
  const showPackageUsage = getShowPackageUsage();
  const showDepositInfo = isDeposit;

  // State for UI
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);

  // Loading states
  const [isCheckingPackages, setIsCheckingPackages] = useState(false);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);

  // Package payment flow state
  const [showOTPSheet, setShowOTPSheet] = useState(false);
  const [showNoPackageSheet, setShowNoPackageSheet] = useState(false);
  const [showPendingPackageUsageSheet, setShowPendingPackageUsageSheet] =
    useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showPromptPayModal, setShowPromptPayModal] = useState(false);
  const [showGenericError, setShowGenericError] = useState(false);
  const [genericErrorMessage, setGenericErrorMessage] = useState('');
  const [promptPayBookingState, setPromptPayBookingState] =
    useState<BookingPaymentLocationState | null>(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const hasInitializedProfileDefaults = useRef(false);
  const [profileLockedFields, setProfileLockedFields] = useState({
    tel: false,
  });

  useEffect(() => {
    const setup = async () => {
      const brandData = await BookingService.getBrandInfo();
      setBrand(brandData);
      setBrandStoreId(storeId);
      await BookingService.validateBooking();
    };

    if (storeId > 0) {
      setup();
    }
  }, [storeId, setBrand, setBrandStoreId]);

  const formatPackageValue = (
    value: number,
    type: PackageType,
    kind: 'amount' | 'remaining',
  ): string => {
    const base = value.toLocaleString();

    switch (type) {
      case PackageType.CashWallet:
        return kind === 'amount' ? `${base} ${t('package.unit.baht')}` : base;
      case PackageType.Times:
        return `${base} ${t('package.unit.times')}`;
      case PackageType.Minutes:
        return kind === 'amount'
          ? `${base} ${t('package.unit.minutes')}`
          : base;
      case PackageType.Percent:
        return kind === 'amount' ? `${base}%` : base;
      default:
        return base;
    }
  };

  const appliedPackages = selectedPackages.map((selectedPkg) => {
    const packageType = selectedPkg.type;

    return {
      id: selectedPkg.id,
      name: selectedPkg.name || `Package #${selectedPkg.id}`,
      usedValue: packageType
        ? formatPackageValue(selectedPkg.value, packageType, 'amount')
        : selectedPkg.value.toLocaleString(),
      remainingValue:
        packageType && selectedPkg.remainingValue !== undefined
          ? formatPackageValue(
              selectedPkg.remainingValue,
              packageType,
              'remaining',
            )
          : null,
    };
  });

  const promotionRows =
    balance.promotions.length +
    (balance.promotionDiscount > 0 && balance.promotions.length > 1 ? 1 : 0);
  const packageRows = selectedPackages.length > 0 ? 1 : 0;
  const stickyFooterSummaryRowCount = Math.min(
    4,
    2 + promotionRows + packageRows,
  );
  const mobileFooterPaddingClass =
    stickyFooterSummaryRowCount >= 4
      ? 'pb-40'
      : stickyFooterSummaryRowCount === 3
        ? 'pb-32'
        : 'pb-26';

  // Payment methods data
  const paymentMethods = [
    {
      id: PaymentMethodType.Promptpay,
      label: t('payment.promptpay'),
      icon: (
        <img src="/icons/promptpay.png" alt="PromptPay" className="size-6" />
      ),
      disabled: false,
    },
    // {
    //   id: 'creditCard',
    //   label: 'Credit Card',
    //   icon: <img src="/icons/creditcard.png" alt="Credit Card" className="size-6" />,
    //   disabled: false,
    // },
    // {
    //   id: 'alipay',
    //   label: 'Alipay',
    //   icon: <img src="/icons/alipay.png" alt="Alipay" className="size-6" />,
    //   disabled: false,
    // },
  ];

  // Form setup
  const {
    register,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(createPaymentFormSchema(false)),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: contactName || '',
      email: contactEmail || '',
      tel: contactTel || '',
      telPrefix: contactTelPrefix,
      // Initialize form paymentMethod from booking store if available
      paymentMethod:
        storePaymentMethod !== undefined && storePaymentMethod !== 0
          ? storePaymentMethod
          : undefined,
    },
  });

  // Ensure form stays in sync if booking store is rehydrated after component mount
  useEffect(() => {
    if (storePaymentMethod !== undefined && storePaymentMethod !== 0) {
      setValue('paymentMethod', storePaymentMethod);
    }
  }, [storePaymentMethod, setValue]);

  useEffect(() => {
    if (hasInitializedProfileDefaults.current) {
      return;
    }

    if (!isLoggedIn || !storedProfile) {
      return;
    }

    const isBlank = (value?: string) => !value?.trim();
    const currentValues = getValues();
    const contactInfoUpdates: Parameters<typeof setContactInfo>[0] = {};

    if (isBlank(currentValues.name) && storedProfile.name) {
      contactInfoUpdates.name = storedProfile.name;
      setValue('name', storedProfile.name, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (isBlank(currentValues.email) && storedProfile.email) {
      contactInfoUpdates.email = storedProfile.email;
      setValue('email', storedProfile.email, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (isBlank(currentValues.tel) && storedProfile.tel) {
      contactInfoUpdates.tel = storedProfile.tel;
      setValue('tel', storedProfile.tel, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setProfileLockedFields((prev) => ({ ...prev, tel: true }));
    }

    if (
      (isBlank(currentValues.tel) || isBlank(currentValues.telPrefix)) &&
      storedProfile.telPrefix
    ) {
      contactInfoUpdates.telPrefix = storedProfile.telPrefix;
      setValue('telPrefix', storedProfile.telPrefix, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    if (Object.keys(contactInfoUpdates).length > 0) {
      setContactInfo(contactInfoUpdates);
    }
    hasInitializedProfileDefaults.current = true;
  }, [getValues, isLoggedIn, setContactInfo, setValue, storedProfile]);

  const watchedTel = watch('tel');
  const isPhoneLocked =
    isLoggedIn &&
    Boolean(storedProfile?.tel) &&
    (profileLockedFields.tel || watchedTel === storedProfile?.tel);

  const selectedPaymentMethod = watch('paymentMethod');

  const renderAppointmentInfo = (className?: string) => (
    <AppointmentInfo className={className} />
  );

  const handleConfirmBookingClick = async () => {
    const isValid = await trigger();
    if (isValid) {
      if (getShowCancellationPolicy()) {
        setShowCancellationModal(true);
      } else {
        handleCancellationPolicyAccept();
      }
    }
  };

  const handleCancellationPolicyAccept = () => {
    setShowCancellationModal(false);
    handleSubmit(onSubmit)();
  };

  const renderBookingCostSummary = () => (
    <BookingCostSummary onConfirm={handleConfirmBookingClick} />
  );

  const isPendingPackageUsageConflict = (error: unknown): boolean => {
    if (typeof error === 'object' && error !== null && 'response' in error) {
      const response = (error as { response?: { status?: number } }).response;
      return response?.status === 409;
    }

    return false;
  };

  // Event handlers
  const onSubmit = async (data: PaymentFormData) => {
    const getLineUserIdForBooking = async (): Promise<string | undefined> => {
      const liffProfile = await getLiffProfile();
      return liffProfile?.userId;
    };

    const buildBookingData = async (paymentMethod: PaymentMethodType) => {
      const lineUserId = await getLineUserIdForBooking();

      return {
        customers,
        availabilityId,
        name: data.name,
        telPrefix: data.telPrefix || '+66',
        tel: data.tel || '',
        email: data.email,
        selectedPackages,
        paymentMethod,
        ...(lineUserId ? { lineUserId } : {}),
      };
    };

    // Save form data to booking store
    setContactInfo({
      name: data.name,
      email: data.email,
      tel: data.tel,
      telPrefix: data.telPrefix,
    });

    // If payment type is 'none' or total is 0, submit booking with payment method 0 and redirect to Thank You
    if (!requirePaymentMethod) {
      try {
        setIsSubmittingBooking(true);
        const bookingData = await buildBookingData(PaymentMethodType.Empty);
        const response = await BookingService.submitBooking(bookingData);
        setBookingCode(response.bookingCode);
        navigate('/booking/thank-you');
      } catch (e) {
        console.error('Failed to create booking', e);
        if (isPendingPackageUsageConflict(e)) {
          setShowPendingPackageUsageSheet(true);
          return;
        }

        setShowGenericError(true);
        setGenericErrorMessage(
          getApiErrorMessage(e, t('booking.error.generic')),
        );
      } finally {
        setIsSubmittingBooking(false);
      }
      return;
    }

    // The form schema ensures `paymentMethod` is a number, use it directly
    const selectedMethodId = (data.paymentMethod ||
      PaymentMethodType.Online) as PaymentMethodType;

    // Navigate to appropriate payment page based on selected numeric method

    try {
      setIsSubmittingBooking(true);
      const bookingData = await buildBookingData(selectedMethodId);
      const response = await BookingService.submitBooking(bookingData);
      if (selectedMethodId === PaymentMethodType.Promptpay) {
        setBookingCode(response.bookingCode);
        const navState: BookingPaymentLocationState = {
          bookingCode: response.bookingCode,
          paymentQrImageBase64: response.paymentQrImageBase64,
          paymentQrData: response.paymentQrData,
          paymentQrExpiresAt: response.paymentQrExpiresAt,
        };
        setPromptPayBookingState(navState);
        setShowPromptPayModal(true);
      } else if (
        selectedMethodId === PaymentMethodType.Online &&
        response.paymentUrl
      ) {
        window.location.href = response.paymentUrl;
      }
    } catch (e) {
      console.error('Failed to create booking for PromptPay', e);
      if (isPendingPackageUsageConflict(e)) {
        setShowPendingPackageUsageSheet(true);
        return;
      }

      setShowGenericError(true);
      setGenericErrorMessage(getApiErrorMessage(e, t('booking.error.generic')));
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const handleAddDetails = (customerIndex: number) => {
    setSelectedCustomerIndex(customerIndex);
    setShowDetailsSheet(true);
  };

  const handlePackageStatus = (statusCode?: string) => {
    if (statusCode === 'NO_PACKAGE') {
      setShowNoPackageSheet(true);
      return;
    }

    if (statusCode === 'PENDING_PAYMENT_PACKAGE_USAGE') {
      setShowPendingPackageUsageSheet(true);
      return;
    }

    setShowPackageModal(true);
  };

  const handleUsePackage = async () => {
    const isPhoneValid = await trigger(['tel', 'telPrefix']);
    if (!isPhoneValid) {
      return;
    }

    const formData = getValues();
    setContactInfo({
      name: formData.name,
      email: formData.email,
      tel: formData.tel,
      telPrefix: formData.telPrefix,
    });

    if (showNoPackageSheet) {
      setShowNoPackageSheet(false);
    }

    if (showPendingPackageUsageSheet) {
      setShowPendingPackageUsageSheet(false);
    }

    const telPrefix = formData.telPrefix || '+66';
    const tel = formData.tel || '';
    const phoneNumber = telPrefix + tel;

    const otpAlreadyValidatedForPhone =
      BookingService.isOTPValidated() && verifiedPhoneNumber === phoneNumber;

    if (ProfileService.isLoggedIn()) {
      try {
        setIsCheckingPackages(true);
        const response = await ProfileService.getMyCustomerPackagesStatus();
        handlePackageStatus(response.code);
      } catch (error: unknown) {
        console.error('Failed to check customer packages:', error);
        showErrorToast(getApiErrorMessage(error, t('booking.error.generic')));
      } finally {
        setIsCheckingPackages(false);
      }
      return;
    }

    if (otpAlreadyValidatedForPhone) {
      try {
        setIsCheckingPackages(true);
        const response =
          await BookingService.getCustomerPackagesStatus(phoneNumber);

        handlePackageStatus(response.code);
      } catch (error: unknown) {
        console.error('Failed to check customer packages:', error);
        if (isAxiosError(error)) {
          setShowOTPSheet(true);
          return;
        }
        showErrorToast(getApiErrorMessage(error, t('booking.error.generic')));
      } finally {
        setIsCheckingPackages(false);
      }
      return;
    }

    setShowOTPSheet(true);
  };

  const handleRemovePackage = async () => {
    const finalValidation = await BookingService.validatePackages([]);
    BookingService.persistValidatedPackages(finalValidation);
  };

  // Package payment flow handlers
  const handleOTPSuccess = async () => {
    try {
      setShowOTPSheet(false);
      setIsCheckingPackages(true);

      // Get customer packages after successful OTP verification
      const formData = watch();
      const telPrefix = formData.telPrefix || '+66';
      const tel = formData.tel || '';
      const phoneNumber = telPrefix + tel;

      if (!phoneNumber || phoneNumber.length < 10) {
        console.error('Phone number not available for package check');
        return;
      }

      const response =
        await BookingService.getCustomerPackagesStatus(phoneNumber);

      handlePackageStatus(response.code);
    } catch (error: unknown) {
      console.error('Failed to check customer packages:', error);
      showErrorToast(getApiErrorMessage(error, t('booking.error.generic')));
    } finally {
      setIsCheckingPackages(false);
    }
  };

  const handleOTPClose = () => {
    setShowOTPSheet(false);
  };

  const handleNoPackageClose = () => {
    setShowNoPackageSheet(false);
  };

  const handlePendingPackageUsageClose = () => {
    setShowPendingPackageUsageSheet(false);
  };

  const handleGenericErrorClose = () => {
    setShowGenericError(false);
  };

  const handleSaveDetails = (details: CustomerDetails) => {
    if (selectedCustomerIndex >= 0) {
      // If useContactInfo is true, use root-level contact info
      const customerData = details.useContactInfo
        ? {
            name: contactName || '',
            telPrefix: contactTelPrefix || '+66',
            tel: contactTel || '',
            email: contactEmail || '',
          }
        : {
            name: details.name,
            telPrefix: details.telPrefix,
            tel: details.tel,
            email: details.email,
          };

      updateCustomer(selectedCustomerIndex, {
        ...customers[selectedCustomerIndex],
        ...customerData,
        remark: details.remark || '',
        useContactInfo: details.useContactInfo || false,
        therapistPreference: {
          gender: details.therapistPreference?.gender || 9,
          preferredTherapistIds:
            details.therapistPreference?.preferredTherapistIds || [],
          unpreferredTherapistIds:
            details.therapistPreference?.unpreferredTherapistIds || [],
        },
      });
    }
    setShowDetailsSheet(false);
    setSelectedCustomerIndex(-1);
  };

  return (
    <div className="pb-12">
      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`md:shadow-card flex flex-col gap-4 ${mobileFooterPaddingClass} md:w-2/3 md:rounded-2xl md:p-6`}
        >
          {/* Contact Information Section */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-4">
              <h2 className="text-primary-text text-lg leading-7 font-semibold">
                {t('payment.contactInformation')}
                <span className="text-error">*</span>
              </h2>
              <p className="text-secondary-text leading-6">
                {t('payment.recommendedPackageHolder')}
              </p>
            </div>

            <div className="space-y-4">
              <TextInput
                label={t('payment.fullName')}
                placeholder={t('payment.fullNamePlaceholder')}
                prefixIcon={<BsPerson className="size-4 text-gray-400" />}
                error={
                  errors.name?.message ? t(errors.name.message) : undefined
                }
                {...register('name', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setContactInfo({ name: e.target.value });
                  },
                })}
              />

              <div className="relative">
                <PhoneNumberInput
                  label={t('payment.phoneNumber')}
                  placeholder={t('payment.phonePlaceholder')}
                  telPrefix={watch('telPrefix') || '+66'}
                  value={watch('tel') || ''}
                  readOnly={isPhoneLocked}
                  onChange={(val: PhoneNumberInputValue) => {
                    setValue('telPrefix', val.dialCode, {
                      shouldValidate: false,
                      shouldDirty: true,
                    });
                    setValue('tel', val.national, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    // Save to booking store immediately
                    setContactInfo({
                      telPrefix: val.dialCode,
                      tel: val.national,
                    });
                  }}
                  error={
                    errors.tel?.message ? t(errors.tel.message) : undefined
                  }
                />
              </div>

              <TextInput
                label={t('payment.email')}
                type="email"
                placeholder={t('payment.emailPlaceholder')}
                prefixIcon={<BsEnvelope className="size-4 text-gray-400" />}
                error={
                  errors.email?.message ? t(errors.email.message) : undefined
                }
                {...register('email', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setContactInfo({ email: e.target.value });
                  },
                })}
              />
            </div>
          </div>

          {/* Appointment Info Section */}
          {renderAppointmentInfo('md:hidden')}

          {/* Booking Details Section */}
          <BookingDetails onAddDetails={handleAddDetails} />

          {/* Package Usage Section - Only shown when showPackageUsage is true */}
          {showPackageUsage && (
            <div className="border-border-inactive rounded-lg border bg-white p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-primary-text text-lg leading-7 font-semibold">
                    {appliedPackages.length > 0
                      ? t('payment.packageApplied')
                      : t('payment.yourPackage')}
                  </h2>

                  {appliedPackages.length > 0 && (
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={handleUsePackage}
                        className="text-primary-text flex cursor-pointer items-center gap-1 text-base leading-6 font-semibold"
                      >
                        <BsPencilSquare className="size-4" />
                        {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        onClick={handleRemovePackage}
                        className="text-primary-text flex cursor-pointer items-center gap-1 text-base leading-6 font-semibold"
                      >
                        <FaRegTrashAlt className="size-4" />
                        {t('common.delete')}
                      </button>
                    </div>
                  )}
                </div>

                {appliedPackages.length === 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className="gap-2 text-base leading-7"
                    onClick={handleUsePackage}
                  >
                    <FaCrown className="text-text-muted size-5" />
                    {t('payment.usePackage')}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    {appliedPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className="border-border-inactive flex items-start justify-between gap-2 rounded-lg border px-2 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-primary-text truncate leading-6 font-medium">
                            {pkg.name}
                          </p>
                          {pkg.remainingValue && (
                            <p className="text-secondary-text truncate leading-6">
                              ({pkg.remainingValue})
                            </p>
                          )}
                        </div>

                        <div className="text-error shrink-0 text-right leading-6 font-medium">
                          {pkg.usedValue}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deposit Info Section */}
          {showDepositInfo && (
            <div className="bg-warning-bg rounded-lg p-4">
              <div className="flex items-start gap-1">
                <CiCircleInfo className="text-warning flex-shrink-0" />
                <div>
                  <div className="text-warning font-normal">
                    {t(
                      `payment.deposit.${
                        {
                          [DepositMode.Fixed]: 'perBooking',
                          [DepositMode.PerCustomer]: 'perCustomer',
                          [DepositMode.Percent]: 'percent',
                        }[depositMode]
                      }`,
                      {
                        value: [
                          depositMode === DepositMode.Percent
                            ? ''
                            : t('common.currency'),
                          balance.depositValue,
                          depositMode === DepositMode.Percent ? '%' : '',
                        ].join(''),
                      },
                    )}
                  </div>
                  <div className="text-secondary font-normal">
                    {t('payment.deposit.detail')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Section - Will be discussed later */}
          {!paymentMethods.length && (
            <div className="border-border-inactive mb-10 rounded-lg border bg-white p-4">
              <div className="space-y-6">
                <h2 className="text-primary-text text-lg leading-7 font-semibold">
                  {t('payment.selectPaymentMethod')}
                </h2>

                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <PaymentMethodOption
                      key={method.id}
                      id={method.id}
                      label={method.label}
                      icon={method.icon}
                      isSelected={selectedPaymentMethod === method.id}
                      onSelect={() => {
                        setValue('paymentMethod', method.id);
                        setPaymentMethod(method.id);
                      }}
                      disabled={method.disabled}
                    />
                  ))}
                </div>

                {errors.paymentMethod && (
                  <p className="text-error text-sm">
                    {t('payment.paymentRequired')}
                  </p>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Right side bar in desktop */}
        <div className="md:shadow-card hidden h-fit space-y-4 md:block md:w-1/3 md:rounded-2xl md:p-6">
          {renderAppointmentInfo('border-0 !p-0')}
          <hr className="border-t border-gray-200" />
          {renderBookingCostSummary()}
        </div>
      </div>

      {/* Sticky Footer */}
      <StickyFooter className="md:hidden">
        {renderBookingCostSummary()}
      </StickyFooter>

      {/* Customer Details Bottom Sheet (Responsive) */}
      <CustomerDetailsFormModalBottomSheet
        isOpen={showDetailsSheet}
        onClose={() => setShowDetailsSheet(false)}
        onSave={handleSaveDetails}
        customerIndex={selectedCustomerIndex}
        initialData={
          selectedCustomerIndex >= 0 && customers[selectedCustomerIndex]
            ? {
                name: customers[selectedCustomerIndex].name,
                email: customers[selectedCustomerIndex].email,
                tel: customers[selectedCustomerIndex].tel,
                telPrefix: customers[selectedCustomerIndex].telPrefix,
                remark: customers[selectedCustomerIndex].remark,
                useContactInfo: customers[selectedCustomerIndex].useContactInfo,
                therapistPreference:
                  customers[selectedCustomerIndex].therapistPreference,
              }
            : {}
        }
      />

      {/* OTP Bottom Sheet for Package Payment */}
      <OTPBottomSheet
        isOpen={showOTPSheet}
        onClose={handleOTPClose}
        phoneNumber={(contactTelPrefix || '') + (contactTel || '')}
        onSuccess={handleOTPSuccess}
      />

      {/* No Package Bottom Sheet */}
      <NoPackageBottomSheet
        isOpen={showNoPackageSheet}
        onClose={handleNoPackageClose}
      />
      <ResponsiveModalBottomSheet
        isOpen={showGenericError}
        title={
          <div className="text-error flex items-center gap-2">
            <FaExclamationTriangle />
            <p>Unknown Error Occured</p>
          </div>
        }
        onClose={handleGenericErrorClose}
      >
        <div className="p-4">
          <p className="text-lg">{genericErrorMessage}</p>
        </div>
      </ResponsiveModalBottomSheet>
      <ResponsiveModalBottomSheet
        isOpen={showPendingPackageUsageSheet}
        onClose={handlePendingPackageUsageClose}
      >
        <div className="flex flex-col gap-6 px-4 pb-6 text-center">
          <div className="flex flex-col gap-4">
            <p className="text-xl leading-7 font-semibold text-black">
              {t('package.pendingPaymentUsage.title')}
            </p>
            <p className="text-secondary-text leading-7">
              {t('package.pendingPaymentUsage.description')}
            </p>
          </div>
          <Button onClick={handlePendingPackageUsageClose}>
            {t('common.ok')}
          </Button>
        </div>
      </ResponsiveModalBottomSheet>
      <PackageSelectionModalPage
        isOpen={showPackageModal}
        onClose={() => setShowPackageModal(false)}
      />
      <PromptPayPaymentModalPage
        isOpen={showPromptPayModal}
        onClose={() => setShowPromptPayModal(false)}
        bookingState={promptPayBookingState}
      />
      <CancellationPolicyModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onAccept={handleCancellationPolicyAccept}
      />
      <LoadingOverlay isVisible={isCheckingPackages || isSubmittingBooking} />
    </div>
  );
};

export default PaymentSummary;
